import { join } from 'node:path';
import { variant } from 'variant';
import type { VariantOf } from 'variant';
import { z } from 'zod';
import {
  ComponentType,
  EmbedCategory,
  EmbeddedScriptPlacement,
  Width as PanelSizeWidth,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { isValidSentryDsn } from '@wix/monitoring-buildtime';

const DIST_DIR = 'dist';
const GIT_IGNORED_DIR = '.wix';
const BLOCKS_DIR = 'blocks';
const SRC_DIR = 'src';
const VITE_SSR_CJS_DEPS = 'сjs-ssr-deps';
const VITE_OPTIMIZED_DEPS = 'optimized-deps';
const VITE_BACKEND_DEPS = '.vite-backend';
const CUSTOM_ELEMENTS_DIR = 'custom-elements';

export const BACKEND_OPTIMIZED_DIR = join(
  'node_modules',
  VITE_BACKEND_DEPS,
  VITE_OPTIMIZED_DEPS
);
export const BACKEND_CJS_DIR = join(
  'node_modules',
  VITE_BACKEND_DEPS,
  VITE_SSR_CJS_DEPS
);
export const APP_CONFIG_PATH = join(GIT_IGNORED_DIR, 'app.config.json');
export const BLOCKS_TYPES_PATH = join(
  GIT_IGNORED_DIR,
  BLOCKS_DIR,
  'types.d.ts'
);

const DASHBOARD_DIR = join(SRC_DIR, 'dashboard');
export const DASHBOARD_PAGES_DIR = join(DASHBOARD_DIR, 'pages');
export const DASHBOARD_PLUGINS_DIR = join(DASHBOARD_DIR, 'plugins');
export const DASHBOARD_MENU_PLUGINS_DIR = join(DASHBOARD_DIR, 'menu-plugins');
export const DASHBOARD_MODALS_DIR = join(DASHBOARD_DIR, 'modals');

export const BACKEND_DIR = join(SRC_DIR, 'backend');
export const API_DIR = join(BACKEND_DIR, 'api');
export const WEB_METHOD_DIR = SRC_DIR;
export const EVENTS_DIR = join(BACKEND_DIR, 'events');
export const SERVICE_PLUGINS_DIR = join(BACKEND_DIR, 'service-plugins');

const SITE_DIR = join(SRC_DIR, 'site');

export const EMBEDDED_SCRIPTS_DIR = join(SITE_DIR, 'embedded-scripts');
export const SITE_COMPONENTS_DIR = join(SITE_DIR, 'components');
export const SITE_WIDGETS_DIR = join(SITE_DIR, 'widgets', CUSTOM_ELEMENTS_DIR);
export const SITE_PLUGINS_DIR = join(SITE_DIR, 'plugins', CUSTOM_ELEMENTS_DIR);
export const BLOCKS_WIDGETS_DIR = join(SITE_DIR, 'widgets', 'blocks');
export const PANELS_DIR = 'panels';

export const WORKER_STATICS_FOLDER = 'worker';
export const BROWSER_STATICS_FOLDER = 'browser';
export const SSR_STATICS_FOLDER = 'ssr';

export const RELATIVE_ASSET_PATH_REGEX =
  /^(?:\.\.?[\\/])*[\w\-+]+([\\/][\w\-+]+)*\.(jpg|svg|png)$/;
const HTTP_REGEX = /^(https?):\/\/[^\s/$.?#].[^\s]*$/;

export function getBackendCJSDir(projectFolder: string) {
  return join(projectFolder, BACKEND_CJS_DIR);
}

export function getDashboardDir(projectFolder: string) {
  return join(projectFolder, DASHBOARD_DIR);
}

export function getDashboardPagesDir(projectFolder: string) {
  return join(projectFolder, DASHBOARD_PAGES_DIR);
}

export function getDashboardPluginsDir(projectFolder: string) {
  return join(projectFolder, DASHBOARD_PLUGINS_DIR);
}

export function getDashboardMenuPluginsDir(projectFolder: string) {
  return join(projectFolder, DASHBOARD_MENU_PLUGINS_DIR);
}

export function getDashboardModalsDir(projectFolder: string) {
  return join(projectFolder, DASHBOARD_MODALS_DIR);
}

export function getSiteDir(projectFolder: string) {
  return join(projectFolder, SITE_DIR);
}

export function getEmbeddedScriptsDir(projectFolder: string) {
  return join(projectFolder, EMBEDDED_SCRIPTS_DIR);
}

export function getSiteComponentsDir(projectFolder: string) {
  return join(projectFolder, SITE_COMPONENTS_DIR);
}

export function getSiteWidgetsDir(projectFolder: string) {
  return join(projectFolder, SITE_WIDGETS_DIR);
}

export function getSitePluginsDir(projectFolder: string) {
  return join(projectFolder, SITE_PLUGINS_DIR);
}

export function getBlocksWidgetsDir(projectFolder: string) {
  return join(projectFolder, BLOCKS_WIDGETS_DIR);
}

export function getBackendFolder(projectFolder: string) {
  return join(projectFolder, BACKEND_DIR);
}

export function getApiFolder(projectFolder: string) {
  return join(projectFolder, API_DIR);
}

export function getWebMethodFolder(projectFolder: string) {
  return join(projectFolder, WEB_METHOD_DIR);
}

export function getEventsFolder(projectFolder: string) {
  return join(projectFolder, EVENTS_DIR);
}

export function getServicePluginsFolder(projectFolder: string) {
  return join(projectFolder, SERVICE_PLUGINS_DIR);
}

export function getDistFolder(projectFolder: string) {
  return join(projectFolder, DIST_DIR);
}

export function getAppBuildFolder(projectFolder: string) {
  return join(getDistFolder(projectFolder), 'statics');
}

export function getAppBrowserBuildFolder(projectFolder: string) {
  return join(getAppBuildFolder(projectFolder), BROWSER_STATICS_FOLDER);
}

export function getAppWorkerBuildFolder(projectFolder: string) {
  return join(getAppBuildFolder(projectFolder), WORKER_STATICS_FOLDER);
}

export function getAppSsrBuildFolder(projectFolder: string) {
  return join(getAppBuildFolder(projectFolder), SSR_STATICS_FOLDER);
}

export function getAppBackendBuildFolder(projectFolder: string) {
  return join(getDistFolder(projectFolder), 'backend');
}

export function getAppConfigPath(projectFolder: string) {
  return join(projectFolder, APP_CONFIG_PATH);
}

export function getBlocksTypesPath(projectFolder: string) {
  return join(projectFolder, BLOCKS_TYPES_PATH);
}

export function getAppManifestPath(projectFolder: string) {
  return join(getDistFolder(projectFolder), 'devcenter', 'app-manifest.json');
}

export function getPackageJsonPath(projectFolder: string) {
  return join(projectFolder, 'package.json');
}

const dashboardSidebarCategoryConfig = z.object({
  id: z.string(),
  label: z.string(),
  priority: z.number().int().optional(),
});

export type DashboardSidebarCategoryConfig = z.infer<
  typeof dashboardSidebarCategoryConfig
>;

const dashboardConfigSchema = z.object({
  sidebar: z
    .object({
      categories: z.array(dashboardSidebarCategoryConfig).optional(),
    })
    .optional(),
});

const baseConfig = {
  $schema: z.string().optional(),
};

const monitoringPublicConfigSchema = z.object({
  sentry: z.object({
    dsn: z.string().refine(isValidSentryDsn, {
      message: 'Invalid Sentry DSN, please make sure the Sentry DSN is correct',
    }),
  }),
});

const monitoringConfigSchema = monitoringPublicConfigSchema
  .extend({ custom: z.unknown() })
  .partial()
  .superRefine((data, ctx) => {
    if (!data.sentry && !data.custom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "monitoring can't be empty",
      });
    }
    if (data.sentry && data.custom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "monitoring can't have both sentry and custom",
      });
    }
  })
  .pipe(
    z.union([
      z.object({ sentry: z.object({ dsn: z.string() }) }),
      z.object({ custom: z.unknown() }),
    ])
  );

export type MonitoringConfig = z.infer<typeof monitoringConfigSchema>;

const blocksWixConfigSchema = z.object({
  uiRevision: z.string(),
});

export const wixPublicConfigSchema = z
  .object({
    ...baseConfig,
    appId: z.string().uuid(),
    projectId: z.string(),
    dashboard: dashboardConfigSchema.optional(),
    monitoring: monitoringPublicConfigSchema.optional(),
    blocks: blocksWixConfigSchema.optional(),
  })
  .strict();

export const wixConfigSchema = wixPublicConfigSchema
  .extend({
    sentryDSN: z.string().optional(),
    trusted: z.boolean().optional(),
    monitoring: monitoringConfigSchema.optional(),
  })
  .strict();

export type WixConfig = z.infer<typeof wixConfigSchema>;

export const tsConfigSchema = z
  .object({
    include: z.array(z.string()).optional(),
  })
  .passthrough();

export const appConfigSchema = z.object({
  siteId: z.string().uuid().optional(),
  devArmTag: z.string().optional(),
  devSiteOverrideId: z.string().optional(),
  topologyComponentId: z.string().uuid().optional(),
  monitoringComponentId: z.string().uuid().optional(),
  mainBlocksComponentId: z.string().uuid().optional(),
});
export type AppConfig = z.infer<typeof appConfigSchema>;

export const staticAssetConfigSchema = z.union([
  z.string().regex(HTTP_REGEX, {
    message: 'The static asset URL must be a valid URL',
  }),
  z.string().regex(RELATIVE_ASSET_PATH_REGEX, {
    message:
      'The static asset must be a relative path to an image (jpg, png, svg)',
  }),
]);

const sidebarConfigSchema = z.object({
  disabled: z.boolean().optional(),
  categoryId: z.string().uuid().optional(),
  priority: z.number().int().optional(),
  whenActive: z
    .object({
      selectedPageId: z.string().optional(),
      hideSidebar: z.boolean().optional(),
    })
    .optional(),
});

export const dashboardPagePublicConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe(
        'The ID (GUID) is used to register the page in Wix Developers Center. The ID must be unique across all pages in the app.'
      ),
    title: z
      .string()
      .min(1)
      .describe(
        'The page title. The title is used as the browser tab title and as the dashboard sidebar label if the page is configured to appear in the sidebar.'
      ),
    additionalRoutes: z
      .array(z.string())
      .optional()
      .describe(
        'A list of routes that lead to this page (in addition to the main route defined by the file path convention). This is useful if you want to support multiple routes for the same page or for backwards compatibility.'
      ),
    sidebar: sidebarConfigSchema.optional(),
  })
  .strict();

export const dashboardPageConfigSchema = dashboardPagePublicConfigSchema
  .extend({
    requiredPermission: z.string().optional(),
    sentryDSN: z.string().optional(),
    experiments: z
      .object({
        enabledBy: z.string(),
      })
      .optional(),
  })
  .strict();

export type DashboardPageConfig = z.infer<typeof dashboardPageConfigSchema>;

export const dashboardModalPublicConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe(
        'The ID (GUID) is used to register the modal in the Wix App Dashboard. The ID must be unique across all pages in the app.'
      ),
    title: z
      .string()
      .min(1)
      .describe(
        'The title is used to refer to the modal in the Wix App Dashboard.'
      ),
    width: z
      .number()
      .optional()
      .describe('Initial width of the modal while loading, in pixels.'),
    height: z
      .number()
      .optional()
      .describe('Initial height of the modal while loading, in pixels.'),
  })
  .strict();

export const dashboardModalConfigSchema = dashboardModalPublicConfigSchema
  .extend({
    requiredPermission: z.string().optional(),
    sentryDSN: z.string().optional(),
  })
  .strict();

export type DashboardModalConfig = z.infer<typeof dashboardModalConfigSchema>;

export const dashboardPluginPublicConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe(
        'ID (GUID) of the plugin you created using the CLI. This ID is auto-generated by Wix.'
      ),
    title: z.string().min(1).describe('Name of the plugin.'),
    extends: z
      .string()
      .uuid()
      .describe('Slot ID of the dashboard page hosting the plugin.'),
    width: z
      .number()
      .optional()
      .describe('Initial width of the plugin while loading, in pixels.'),
    height: z
      .number()
      .optional()
      .describe('Initial height of the plugin while loading, in pixels.'),
  })
  .strict();

export const dashboardPluginConfigSchema = dashboardPluginPublicConfigSchema
  .extend({
    requiredPermission: z.string().optional(),
    sentryDSN: z.string().optional(),
  })
  .strict();

export type DashboardPluginConfig = z.infer<typeof dashboardPluginConfigSchema>;

export const actionSchema = z
  .object({
    navigateToPage: z
      .object({
        pageId: z
          .string()
          .uuid()
          .describe(
            'ID of the dashboard page to which site administrators are directed.'
          ),
        relativeUrl: z
          .string()
          .optional()
          .describe(
            'URI segment appended to the base URI of the target dashboard page. '
          ),
      })
      .describe('Page navigation configuration object. '),
    openModal: z.object({
      componentId: z
        .string()
        .uuid()
        .describe(
          "ID of the dashboard modal to display when the extension's menu item is clicked."
        ),
      componentParams: z
        .record(z.string().min(1), z.string().min(1))
        .optional()
        .describe(
          'Additional configuration parameters you can pass to the dashboard modal.'
        ),
    }),
  })
  .partial()
  .superRefine((data, ctx) => {
    if (!data.navigateToPage && !data.openModal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'action must have either navigateToPage or openModal',
      });
    }
    if (data.navigateToPage && data.openModal) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "action can't have both navigateToPage and openModal",
      });
    }
  })
  .describe(
    'Navigation configuration object that determines the action taken when the extension is clicked.'
  );

export type ActionSchema = z.infer<typeof actionSchema>;

export const dashboardMenuPluginPublicConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe(
        'The ID (GUID) is used to register the page in the Wix App Dashboard and must be unique across all extensions in the app.'
      ),
    title: z
      .string()
      .min(1)
      .describe('Text of the menu item the extension adds.'),
    subtitle: z
      .string()
      .max(200)
      .optional()
      .describe(
        'Secondary text that appears in the menu just below the title.'
      ),
    iconKey: z
      .string()
      .min(3)
      .max(50)
      .optional()
      .describe("Icon that appears next to your extension's title."),
    extends: z
      .string()
      .uuid()
      .describe('Slot ID into which the extension plugs in.'),
    action: actionSchema,
  })
  .strict();

export const dashboardMenuPluginConfigSchema =
  dashboardMenuPluginPublicConfigSchema
    .extend({
      sentryDSN: z.string().optional(),
      requiredPermission: z.string().optional(),
    })
    .strict();

export type DashboardMenuPluginConfig = z.infer<
  typeof dashboardMenuPluginConfigSchema
>;

export const embeddedScriptConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe('The ID (GUID) is a unique identifier for your script.'),
    name: z
      .string()
      .min(1)
      .describe(
        'The name of your script as it will appear in the Wix App Dashboard.'
      ),
    scriptType: z
      .enum([
        EmbedCategory.FUNCTIONAL,
        EmbedCategory.ESSENTIAL,
        EmbedCategory.ANALYTICS,
        EmbedCategory.ADVERTISING,
      ])
      .describe(
        "An enum used by Wix's Cookie Consent Banner tool to determine whether site visitors consent to having your script run during their visit."
      ),
    placement: z
      .enum([
        EmbeddedScriptPlacement.HEAD,
        EmbeddedScriptPlacement.BODY_START,
        EmbeddedScriptPlacement.BODY_END,
      ])
      .describe(
        "An enum indicating where in the page's DOM the HTML code will be injected."
      ),
  })
  .strict();

export type EmbeddedScriptConfig = z.infer<typeof embeddedScriptConfigSchema>;

export const PlatformType = variant({
  Site: {},
  Editor: {},
  Dashboard: {},
  Blocks: {},
});

export type PlatformType = VariantOf<typeof PlatformType>;

export const customElementPresetSchema = z
  .object({
    id: z.string().uuid(),
    thumbnailUrl: staticAssetConfigSchema,
  })
  .strict();

export type CustomElementPresetConfig = z.infer<
  typeof customElementPresetSchema
>;

export const customElementConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe('The ID (GUID) is a unique identifier for your element.'),
    name: z
      .string()
      .min(1)
      .max(30)
      .describe(
        'The name of your element as it will appear in the Wix App Dashboard'
      ),
    height: z
      .object({
        defaultHeight: z
          .number()
          .describe(
            'Initial height of the widget in pixels when it is first installed on a site'
          ),
      })
      .strict(),
    width: z
      .object({
        defaultWidth: z
          .number()
          .describe(
            'Initial width of the widget in pixels when it is first installed on a site'
          ),
        stretchByDefault: z
          .boolean()
          .optional()
          .describe(
            "Sets the widget's stretch on installation. If set to true, it will stretch the widget to the full width of its container"
          ),
        allowStretch: z
          .boolean()
          .optional()
          .describe(
            'Allows a user to stretch the widget to the full width of the container if true'
          ),
      })
      .strict(),
    installation: z
      .object({
        autoAddToSite: z
          .boolean()
          .optional()
          .describe(
            'Automatically adds the widget to a site upon installation if true'
          ),
        essential: z
          .boolean()
          .optional()
          .describe(
            "Marks the widget as crucial to your app's functionality or not. If true and a user deletes the widget, the entire app will also be deleted."
          ),
      })
      .strict()
      .optional(),
    presets: z
      .array(customElementPresetSchema)
      .length(1, 'Only one preset is allowed at the moment')
      .optional(),
    behaviors: z
      .object({
        dashboard: z
          .object({
            dashboardPageComponentId: z
              .string()
              .uuid()
              .describe('The ID of the dashboard page component'),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export type CustomElementConfig = z.infer<typeof customElementConfigSchema>;

export const sitePluginPlacementSchema = z
  .object({
    appDefinitionId: z.string().uuid(),
    widgetId: z.string().uuid(),
    slotId: z.string(),
  })
  .strict();

export type SitePluginPlacementConfig = z.infer<
  typeof sitePluginPlacementSchema
>;

export const sitePluginConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe('The ID (GUID) is a unique identifier for your plugin.'),
    referenceComponentId: z
      .string()
      .uuid()
      .describe(
        'The referenceComponentId (GUID) is a unique identifier for your element.'
      ),
    marketData: z
      .object({
        name: z
          .string()
          .max(30)
          .describe(
            'The name of your plugin as it will appear in the plugin explorer'
          ),
        description: z
          .string()
          .optional()
          .describe(
            'The description of your plugin as it will appear in the plugin explorer'
          ),
        logoUrl: z
          .string()
          .optional()
          .transform((val, ctx) => {
            if (val?.includes('{{PUBLIC_URL}}')) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  '`{{PUBLIC_URL}}` is no longer supported, please update `marketData.logoUrl` to a relative path, see more at: https://wix.to/HSMtU5x',
              });

              return z.NEVER;
            }

            return val;
          })
          .pipe(
            staticAssetConfigSchema
              .optional()
              .describe(
                'The logo of your plugin as it will appear in the plugin explorer'
              )
          ),
      })
      .strict(),
    placements: z.array(sitePluginPlacementSchema),
    installation: z
      .object({
        autoAddToSite: z
          .boolean()
          .optional()
          .describe(
            'Automatically adds the plugin to a site selected slot upon installation if true'
          ),
      })
      .strict()
      .optional(),
  })
  .strict();

export type SitePluginConfig = z.infer<typeof sitePluginConfigSchema>;

export const blocksWidgetConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe('The ID (GUID) is a unique identifier for your element.'),
  })
  .strict();
export type BlocksWidgetConfig = z.infer<typeof blocksWidgetConfigSchema>;

export const blocksWidgetPanelConfigSchema = z.object({
  ...baseConfig,
  id: z
    .string()
    .uuid()
    .describe('The ID (GUID) is a unique identifier for your element.'),
});
export type BlocksWidgetPanelConfig = z.infer<
  typeof blocksWidgetPanelConfigSchema
>;

export const embeddedScriptParamsOverrideSchema = z.record(z.string());

export type EmbeddedScriptParamsOverride = z.infer<
  typeof embeddedScriptParamsOverrideSchema
>;

export const topologySchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
    backendApiUrl: z.string().min(1),
  })
  .strict();

export type TopologyConfig = z.infer<typeof topologySchema>;

export const ecomShippingRatesConfigSchema = z
  .object({
    ...baseConfig,
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    learnMoreUrl: z.string().url().optional(),
    dashboardUrl: z.string().url().optional(),
    fallbackDefinitionMandatory: z.boolean().optional(),
    thumbnailUrl: z.string().url().optional(),
  })
  .strict();

export type EcomShippingRatesConfig = z.infer<
  typeof ecomShippingRatesConfigSchema
>;

export const ecomAdditionalFeesConfigSchema = z
  .object({
    ...baseConfig,
    id: z.string().uuid(),
    name: z.string(),
  })
  .strict();

export type EcomAdditionalFeesConfig = z.infer<
  typeof ecomAdditionalFeesConfigSchema
>;

export const ecomDiscountsTriggerConfigSchema = z
  .object({
    ...baseConfig,
    id: z.string().uuid(),
    name: z.string(),
  })
  .strict();

export type EcomDiscountsTriggerConfig = z.infer<
  typeof ecomDiscountsTriggerConfigSchema
>;

export const ecomValidationsConfigSchema = z
  .object({
    ...baseConfig,
    id: z.string().uuid(),
    name: z.string(),
    validateInCart: z.boolean().optional(),
  })
  .strict();

export type EcomValidationsConfig = z.infer<typeof ecomValidationsConfigSchema>;

export const ecomPaymentSettingsConfigSchema = z
  .object({
    ...baseConfig,
    id: z.string().uuid(),
    name: z.string(),
    fallbackValueForRequires3dSecure: z.boolean().optional(),
  })
  .strict();

export type EcomPaymentSettingsConfig = z.infer<
  typeof ecomPaymentSettingsConfigSchema
>;

export const ecomGiftCardsProviderConfigSchema = z
  .object({
    ...baseConfig,
    id: z.string().uuid(),
    name: z.string(),
  })
  .strict();

export type EcomGiftCardsProviderConfig = z.infer<
  typeof ecomGiftCardsProviderConfigSchema
>;

export const supportedServicePlugin = z.union([
  z.literal(ComponentType.ECOM_SHIPPING_RATES),
  z.literal(ComponentType.ECOM_ADDITIONAL_FEES),
  z.literal(ComponentType.ECOM_DISCOUNTS_TRIGGER),
  z.literal(ComponentType.ECOM_VALIDATIONS),
  z.literal(ComponentType.ECOM_PAYMENT_SETTINGS),
  z.literal(ComponentType.GIFT_CARDS_PROVIDER),
]);

export type SupportedServicePlugin = z.infer<typeof supportedServicePlugin>;

export const siteComponentConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe(
        'The ID (GUID) is used to register the component in Wix Developers Center. The ID must be unique across all extensions in the app.'
      ),
    description: z.string(),
    editorElement: z.unknown(),
  })
  .strict();

export type SiteComponentConfig = z.infer<typeof siteComponentConfigSchema>;

export const siteComponentPanelConfigSchema = z
  .object({
    ...baseConfig,
    id: z
      .string()
      .uuid()
      .describe(
        'The ID (GUID) is used to register the component in Wix Developers Center. The ID must be unique across all extensions in the app.'
      ),
    name: z
      .string()
      .describe('The name of your panel as it will appear in title'),
    width: z
      .enum([PanelSizeWidth.SMALL, PanelSizeWidth.MEDIUM, PanelSizeWidth.LARGE])
      .optional()
      .describe('Initial width of the panel while loading'),
    height: z
      .number()
      .min(150)
      .optional()
      .describe('Initial height of the panel while loading, in pixels.'),
  })
  .strict();

export type SiteComponentPanelConfig = z.infer<
  typeof siteComponentPanelConfigSchema
>;
